// src/pages/PrivacyPolicy.jsx

import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { JsonLd } from "../components/SEO";

const privacyPolicySchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Privacy Policy | Dorado9",
  url: "https://dorado9.com/privacy-policy",
  description:
    "Read the Dorado9 privacy policy and learn how information is handled when you use our website.",
  publisher: { "@id": "https://dorado9.com/#organization" },
};

export default function PrivacyPolicy() {
  return (
    <>
      <JsonLd id="privacy-policy-page" data={privacyPolicySchema} />
      {/* HERO */}
      <section
        className="
          relative
          bg-brand-bg
          pt-36 lg:pt-48
          pb-20 lg:pb-28
          px-6 lg:px-12
          overflow-hidden
        "
      >
        <div
          className="
            absolute
            right-[-10%]
            top-[-20%]
            w-[55vw]
            h-[55vw]
            rounded-full
            pointer-events-none
          "
          style={{
            background:
              "radial-gradient(circle, rgba(102,76,221,0.24), transparent 62%)",
          }}
        />

        <div className="max-w-[1600px] mx-auto relative z-10">
          <Reveal>
            <p className="eyebrow mb-6">
              The Fine Print
            </p>

            <h1
              className="
                font-display
                text-[13vw]
                sm:text-[10vw]
                lg:text-[7.5vw]
                leading-[0.88]
                tracking-tighter
                uppercase
                text-white
                max-w-6xl
              "
            >
              Privacy, without{" "}
              <span className="font-serif-accent text-brand-mint">
                the mystery.
              </span>
            </h1>

            <p
              className="
                mt-10
                max-w-3xl
                text-zinc-400
                text-base
                lg:text-lg
                leading-8
              "
            >
              This Privacy Policy describes our policies and procedures on the
              collection, use and disclosure of your information when you use
              the Service and explains your privacy rights and how the law
              protects you.
            </p>

            <p
              className="
                mt-5
                text-xs
                uppercase
                tracking-[0.24em]
                text-zinc-600
              "
            >
              Last updated: February 13, 2026
            </p>
          </Reveal>
        </div>
      </section>


      {/* POLICY CONTENT */}
      <section
        className="
          relative
          bg-brand-bg
          pb-24 lg:pb-36
          px-6 lg:px-12
        "
      >
        <div
          className="
            max-w-[1500px]
            mx-auto
            grid
            lg:grid-cols-12
            gap-16
            lg:gap-24
            items-start
          "
        >
          {/* LEFT / CONTENTS */}
          <aside
            className="
              lg:col-span-4
              lg:sticky
              lg:top-32
              self-start
            "
          >
            <Reveal>
              <p className="eyebrow mb-6">
                Contents
              </p>

              <nav className="space-y-4">

                <TocLink href="#introduction">
                  01 &nbsp; Introduction
                </TocLink>

                <TocLink href="#definitions">
                  02 &nbsp; Definitions
                </TocLink>

                <TocLink href="#data-collected">
                  03 &nbsp; Data We Collect
                </TocLink>

                <TocLink href="#cookies">
                  04 &nbsp; Cookies & Tracking
                </TocLink>

                <TocLink href="#use-of-data">
                  05 &nbsp; Use of Personal Data
                </TocLink>

                <TocLink href="#retention">
                  06 &nbsp; Data Retention
                </TocLink>

                <TocLink href="#transfer">
                  07 &nbsp; Data Transfers
                </TocLink>

                <TocLink href="#your-rights">
                  08 &nbsp; Your Rights
                </TocLink>

                <TocLink href="#disclosure">
                  09 &nbsp; Disclosure
                </TocLink>

                <TocLink href="#security">
                  10 &nbsp; Security
                </TocLink>

                <TocLink href="#children">
                  11 &nbsp; Children's Privacy
                </TocLink>

                <TocLink href="#external-links">
                  12 &nbsp; External Links
                </TocLink>

                <TocLink href="#changes">
                  13 &nbsp; Changes
                </TocLink>

                <TocLink href="#contact">
                  14 &nbsp; Contact
                </TocLink>

              </nav>
            </Reveal>
          </aside>


          {/* RIGHT / POLICY */}
          <div className="lg:col-span-8 space-y-20">

            {/* 01 */}
            <PolicySection
              id="introduction"
              number="01"
              title="Introduction"
            >
              <p>
                This Privacy Policy describes Our policies and procedures on
                the collection, use and disclosure of Your information when You
                use the Service and tells You about Your privacy rights and how
                the law protects You.
              </p>

              <p>
                We use Your Personal Data to provide and improve the Service. By
                using the Service, You agree to the collection and use of
                information in accordance with this Privacy Policy.
              </p>
            </PolicySection>


            {/* 02 */}
            <PolicySection
              id="definitions"
              number="02"
              title="Interpretation and Definitions"
            >
              <Subheading>Interpretation</Subheading>

              <p>
                The words whose initial letters are capitalized have meanings
                defined under the following conditions. The following
                definitions shall have the same meaning regardless of whether
                they appear in singular or in plural.
              </p>

              <Subheading>Definitions</Subheading>

              <p>
                For the purposes of this Privacy Policy:
              </p>

              <PolicyList>

                <li>
                  <strong>Account</strong> means a unique account created for
                  You to access our Service or parts of our Service.
                </li>

                <li>
                  <strong>Affiliate</strong> means an entity that controls, is
                  controlled by, or is under common control with a party, where
                  "control" means ownership of 50% or more of the shares, equity
                  interest or other securities entitled to vote for election of
                  directors or other managing authority.
                </li>

                <li>
                  <strong>Company</strong> refers to Dorado9, SJS Avenue,
                  Airport Road, Amritsar.
                </li>

                <li>
                  <strong>Cookies</strong> are small files that are placed on
                  Your computer, mobile device or any other device by a website,
                  containing details of Your browsing history on that website.
                </li>

                <li>
                  <strong>Country</strong> refers to Punjab, India.
                </li>

                <li>
                  <strong>Device</strong> means any device that can access the
                  Service such as a computer, mobile phone or digital tablet.
                </li>

                <li>
                  <strong>Personal Data</strong> or "Personal Information"
                  means any information that relates to an identified or
                  identifiable individual.
                </li>

                <li>
                  <strong>Service</strong> refers to the Website.
                </li>

                <li>
                  <strong>Service Provider</strong> means any natural or legal
                  person who processes data on behalf of the Company.
                </li>

                <li>
                  <strong>Usage Data</strong> refers to data collected
                  automatically, either generated by the use of the Service or
                  from the Service infrastructure itself.
                </li>

                <li>
                  <strong>Website</strong> refers to Dorado9, accessible from{" "}
                  <a
                    href="https://dorado9-com-897128.hostingersite.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-mint hover:underline"
                  >
                    dorado9-com-897128.hostingersite.com
                  </a>
                  .
                </li>

                <li>
                  <strong>You</strong> means the individual accessing or using
                  the Service, or the company or other legal entity on behalf
                  of which such individual is accessing or using the Service.
                </li>

              </PolicyList>
            </PolicySection>


            {/* 03 */}
            <PolicySection
              id="data-collected"
              number="03"
              title="Collecting and Using Your Personal Data"
            >
              <Subheading>Types of Data Collected</Subheading>

              <MinorHeading>Personal Data</MinorHeading>

              <p>
                While using Our Service, We may ask You to provide Us with
                certain personally identifiable information that can be used
                to contact or identify You.
              </p>

              <PolicyList>
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Phone number</li>
                <li>Address, State, Province, ZIP/Postal code and City</li>
              </PolicyList>

              <MinorHeading>Usage Data</MinorHeading>

              <p>
                Usage Data is collected automatically when using the Service.
              </p>

              <p>
                Usage Data may include information such as Your Device's
                Internet Protocol address, browser type, browser version, the
                pages of our Service that You visit, the time and date of Your
                visit, the time spent on those pages, unique device identifiers
                and other diagnostic data.
              </p>

              <p>
                When You access the Service by or through a mobile device, We
                may collect certain information automatically, including the
                type of mobile device You use, Your mobile device's unique ID,
                the IP address of Your mobile device, Your mobile operating
                system, the type of mobile Internet browser You use and other
                diagnostic data.
              </p>

              <p>
                We may also collect information that Your browser sends
                whenever You visit Our Service or when You access the Service
                through a mobile device.
              </p>
            </PolicySection>


            {/* 04 */}
            <PolicySection
              id="cookies"
              number="04"
              title="Tracking Technologies and Cookies"
            >
              <p>
                We use Cookies and similar tracking technologies to track the
                activity on Our Service and store certain information.
              </p>

              <PolicyList>

                <li>
                  <strong>Cookies or Browser Cookies.</strong> A cookie is a
                  small file placed on Your Device. You can instruct Your
                  browser to refuse all Cookies or to indicate when a Cookie is
                  being sent.
                </li>

                <li>
                  <strong>Web Beacons.</strong> Certain sections of our Service
                  and our emails may contain small electronic files known as
                  web beacons, clear gifs, pixel tags and single-pixel gifs.
                </li>

              </PolicyList>

              <p>
                Cookies can be "Persistent" or "Session" Cookies. Persistent
                Cookies remain on Your personal computer or mobile device when
                You go offline, while Session Cookies are deleted as soon as
                You close Your web browser.
              </p>

              <p>
                Where required by law, we use non-essential cookies such as
                analytics, advertising and remarketing cookies only with Your
                consent.
              </p>

              <Subheading>Cookies We Use</Subheading>

              <CookieBlock
                title="Necessary / Essential Cookies"
                type="Session Cookies"
              >
                These Cookies are essential to provide You with services
                available through the Website and to enable You to use some of
                its features.
              </CookieBlock>

              <CookieBlock
                title="Cookies Policy / Notice Acceptance Cookies"
                type="Persistent Cookies"
              >
                These Cookies identify if users have accepted the use of
                cookies on the Website.
              </CookieBlock>

              <CookieBlock
                title="Functionality Cookies"
                type="Persistent Cookies"
              >
                These Cookies allow Us to remember choices You make when You
                use the Website, such as remembering login details or language
                preferences.
              </CookieBlock>
            </PolicySection>


            {/* 05 */}
            <PolicySection
              id="use-of-data"
              number="05"
              title="Use of Your Personal Data"
            >
              <p>
                The Company may use Personal Data for the following purposes:
              </p>

              <PolicyList>

                <li>
                  <strong>To provide and maintain our Service</strong>,
                  including monitoring the usage of our Service.
                </li>

                <li>
                  <strong>To manage Your Account</strong>, including Your
                  registration as a user of the Service.
                </li>

                <li>
                  <strong>For the performance of a contract</strong>, including
                  the development, compliance and undertaking of contracts for
                  services purchased through the Service.
                </li>

                <li>
                  <strong>To contact You</strong> by email, telephone calls,
                  SMS or other equivalent forms of electronic communication.
                </li>

                <li>
                  <strong>To provide You</strong> with news, special offers and
                  information about other goods, services and events which We
                  offer unless You have opted not to receive such information.
                </li>

                <li>
                  <strong>To manage Your requests</strong> and respond to
                  enquiries submitted to Us.
                </li>

                <li>
                  <strong>For business transfers</strong>, including evaluating
                  or conducting a merger, restructuring, reorganization or sale
                  of some or all of Our assets.
                </li>

                <li>
                  <strong>For other purposes</strong> including data analysis,
                  identifying usage trends, determining the effectiveness of
                  promotional campaigns and improving our Service, marketing
                  and user experience.
                </li>

              </PolicyList>

              <Subheading>Sharing Your Personal Data</Subheading>

              <PolicyList>

                <li>
                  <strong>With Service Providers:</strong> to monitor and
                  analyze use of the Service and to contact You.
                </li>

                <li>
                  <strong>For business transfers:</strong> during negotiations
                  or completion of a merger, sale, financing or acquisition.
                </li>

                <li>
                  <strong>With Affiliates:</strong> where such affiliates are
                  required to honour this Privacy Policy.
                </li>

                <li>
                  <strong>With business partners:</strong> to offer certain
                  products, services or promotions.
                </li>

                <li>
                  <strong>With other users:</strong> if information is shared
                  in public areas of the Service.
                </li>

                <li>
                  <strong>With Your consent:</strong> for any other purpose
                  where You have provided consent.
                </li>

              </PolicyList>
            </PolicySection>


            {/* 06 */}
            <PolicySection
              id="retention"
              number="06"
              title="Retention of Your Personal Data"
            >
              <p>
                The Company will retain Your Personal Data only for as long as
                is necessary for the purposes set out in this Privacy Policy.
              </p>

              <p>
                We will retain and use Your Personal Data to the extent
                necessary to comply with legal obligations, resolve disputes
                and enforce our legal agreements and policies.
              </p>

              <Subheading>Retention Periods</Subheading>

              <PolicyList>

                <li>
                  <strong>User Accounts:</strong> retained for the duration of
                  your account relationship plus up to 24 months after account
                  closure.
                </li>

                <li>
                  <strong>Support tickets and correspondence:</strong> up to 24
                  months after ticket closure.
                </li>

                <li>
                  <strong>Chat transcripts:</strong> up to 24 months for
                  quality assurance and training.
                </li>

                <li>
                  <strong>Website analytics data:</strong> up to 24 months from
                  collection.
                </li>

                <li>
                  <strong>Server logs:</strong> up to 24 months for security
                  monitoring and troubleshooting.
                </li>

              </PolicyList>

              <p>
                We may retain Personal Data beyond the periods above where
                required by law, for legal claims, at Your explicit request or
                due to technical limitations such as backup systems.
              </p>

              <Subheading>Deletion and Anonymization</Subheading>

              <PolicyList>

                <li>
                  <strong>Deletion:</strong> Personal Data is removed from Our
                  systems and is no longer actively processed.
                </li>

                <li>
                  <strong>Backup retention:</strong> Residual copies may remain
                  in encrypted backups for a limited period.
                </li>

                <li>
                  <strong>Anonymization:</strong> Personal Data may be converted
                  into anonymous statistical data that cannot be linked back to
                  You.
                </li>

              </PolicyList>
            </PolicySection>


            {/* 07 */}
            <PolicySection
              id="transfer"
              number="07"
              title="Transfer of Your Personal Data"
            >
              <p>
                Your information, including Personal Data, is processed at the
                Company's operating offices and in any other places where the
                parties involved in the processing are located.
              </p>

              <p>
                This means that information may be transferred to and
                maintained on computers located outside of Your state,
                province, country or other governmental jurisdiction where data
                protection laws may differ.
              </p>

              <p>
                Where required by applicable law, We will ensure that
                international transfers of Your Personal Data are subject to
                appropriate safeguards.
              </p>
            </PolicySection>


            {/* 08 */}
            <PolicySection
              id="your-rights"
              number="08"
              title="Delete Your Personal Data"
            >
              <p>
                You have the right to delete or request that We assist in
                deleting the Personal Data that We have collected about You.
              </p>

              <p>
                You may update, amend or delete Your information at any time by
                signing in to Your Account, if You have one, and visiting the
                account settings section.
              </p>

              <p>
                You may also contact Us to request access to, correct or delete
                any Personal Data that You have provided to Us.
              </p>

              <p>
                Please note that We may need to retain certain information
                where We have a legal obligation or lawful basis to do so.
              </p>
            </PolicySection>


            {/* 09 */}
            <PolicySection
              id="disclosure"
              number="09"
              title="Disclosure of Your Personal Data"
            >
              <Subheading>Business Transactions</Subheading>

              <p>
                If the Company is involved in a merger, acquisition or asset
                sale, Your Personal Data may be transferred.
              </p>

              <Subheading>Law Enforcement</Subheading>

              <p>
                Under certain circumstances, the Company may be required to
                disclose Your Personal Data if required to do so by law or in
                response to valid requests by public authorities.
              </p>

              <Subheading>Other Legal Requirements</Subheading>

              <PolicyList>
                <li>Comply with a legal obligation</li>
                <li>Protect and defend the rights or property of the Company</li>
                <li>
                  Prevent or investigate possible wrongdoing in connection with
                  the Service
                </li>
                <li>
                  Protect the personal safety of Users of the Service or the
                  public
                </li>
                <li>Protect against legal liability</li>
              </PolicyList>
            </PolicySection>


            {/* 10 */}
            <PolicySection
              id="security"
              number="10"
              title="Security of Your Personal Data"
            >
              <p>
                The security of Your Personal Data is important to Us, but
                remember that no method of transmission over the Internet or
                method of electronic storage is 100% secure.
              </p>

              <p>
                While We strive to use commercially reasonable means to protect
                Your Personal Data, We cannot guarantee its absolute security.
              </p>
            </PolicySection>


            {/* 11 */}
            <PolicySection
              id="children"
              number="11"
              title="Children's Privacy"
            >
              <p>
                Our Service does not address anyone under the age of 16. We do
                not knowingly collect personally identifiable information from
                anyone under the age of 16.
              </p>

              <p>
                If You are a parent or guardian and You are aware that Your
                child has provided Us with Personal Data, please contact Us.
              </p>

              <p>
                If We become aware that We have collected Personal Data from
                anyone under the age of 16 without verification of parental
                consent, We take steps to remove that information from Our
                servers.
              </p>
            </PolicySection>


            {/* 12 */}
            <PolicySection
              id="external-links"
              number="12"
              title="Links to Other Websites"
            >
              <p>
                Our Service may contain links to other websites that are not
                operated by Us.
              </p>

              <p>
                If You click on a third-party link, You will be directed to that
                third party's site. We strongly advise You to review the Privacy
                Policy of every site You visit.
              </p>

              <p>
                We have no control over and assume no responsibility for the
                content, privacy policies or practices of any third-party sites
                or services.
              </p>
            </PolicySection>


            {/* 13 */}
            <PolicySection
              id="changes"
              number="13"
              title="Changes to this Privacy Policy"
            >
              <p>
                We may update Our Privacy Policy from time to time. We will
                notify You of any changes by posting the new Privacy Policy on
                this page.
              </p>

              <p>
                We may also notify You via email and/or a prominent notice on
                Our Service prior to the change becoming effective and update
                the "Last updated" date at the top of this Privacy Policy.
              </p>

              <p>
                You are advised to review this Privacy Policy periodically for
                any changes.
              </p>
            </PolicySection>


            {/* 14 */}
            <PolicySection
              id="contact"
              number="14"
              title="Contact Us"
            >
              <p>
                If you have any questions about this Privacy Policy, You can
                contact us:
              </p>

              <PolicyList>

                <li>
                  By email:{" "}
                  <a
                    href="mailto:hello@dorado9.com"
                    className="text-brand-mint hover:underline"
                  >
                    hello@dorado9.com
                  </a>
                </li>

                <li>
                  By visiting our{" "}
                  <Link
                    to="/contact"
                    className="text-brand-mint hover:underline"
                  >
                    contact page
                  </Link>
                  .
                </li>

                <li>
                  By phone:{" "}
                  <a
                    href="tel:+919855844729"
                    className="text-brand-mint hover:underline"
                  >
                    +91 9855844729
                  </a>
                </li>

              </PolicyList>
            </PolicySection>

          </div>
        </div>
      </section>
    </>
  );
}


function TocLink({ href, children }) {
  return (
    <a
      href={href}
      className="
        block
        text-zinc-400
        hover:text-brand-mint
        transition-colors
      "
    >
      {children}
    </a>
  );
}


function PolicySection({
  id,
  number,
  title,
  children,
}) {
  return (
    <section
      id={id}
      className="scroll-mt-32"
    >
      <Reveal>

        <div className="flex gap-5 items-start mb-7">

          <span
            className="
              font-serif-accent
              text-brand-mint
              text-3xl
              lg:text-4xl
              leading-none
              shrink-0
            "
          >
            {number}
          </span>

          <h2
            className="
              font-display
              text-3xl
              lg:text-5xl
              uppercase
              tracking-tight
              leading-[0.95]
              text-white
            "
          >
            {title}
          </h2>

        </div>

        <div
          className="
            max-w-3xl
            text-zinc-400
            text-base
            lg:text-lg
            leading-8
            space-y-5
          "
        >
          {children}
        </div>

      </Reveal>
    </section>
  );
}


function Subheading({ children }) {
  return (
    <h3
      className="
        mt-10
        mb-4
        font-display
        text-xl
        lg:text-2xl
        uppercase
        tracking-tight
        text-white
      "
    >
      {children}
    </h3>
  );
}


function MinorHeading({ children }) {
  return (
    <h4
      className="
        mt-8
        mb-3
        text-sm
        uppercase
        tracking-[0.22em]
        text-brand-mint
      "
    >
      {children}
    </h4>
  );
}


function PolicyList({ children }) {
  return (
    <ul
      className="
        space-y-3
        pl-5
        list-disc
        marker:text-brand-mint
      "
    >
      {children}
    </ul>
  );
}


function CookieBlock({
  title,
  type,
  children,
}) {
  return (
    <div
      className="
        border
        border-white/[0.06]
        bg-white/[0.015]
        p-6
        lg:p-7
      "
    >
      <h4
        className="
          font-display
          text-xl
          uppercase
          text-white
        "
      >
        {title}
      </h4>

      <p
        className="
          mt-2
          text-xs
          uppercase
          tracking-[0.2em]
          text-brand-mint
        "
      >
        {type}
      </p>

      <p
        className="
          mt-4
          text-zinc-400
          leading-7
        "
      >
        {children}
      </p>
    </div>
  );
}